/**
 * The mobile fleet's unsent backlog, shown on the farm dashboard.
 *
 * Mirrors `GET /device/sync-status` (swagger:
 * `HealthSystem.API.Controllers.Device.Responses.DeviceSyncStatusOverviewResponse`). Hand-written rather
 * than taken from the generated client, for the reason described in `DeviceSyncStatusService`. Field names
 * match the API exactly so the two stay easy to diff.
 *
 * The shape to keep in mind: the phone reports, the backend stores every report, and this endpoint hands
 * back the *latest* report per device. So a row is a device's current state, not an event - and the counts
 * on the overview always cover the whole fleet even when `devices` has been narrowed to the ones behind.
 */
export interface DeviceSyncStatusOverview {
  farmId: string;
  /** Animals still queued across every device that has ever reported for this farm. */
  totalPendingCount: number;
  /** Devices that have ever reported. Zero means the farm does not use the offline queue at all. */
  deviceCount: number;
  /** Devices whose latest report still carried a queue. */
  pendingDeviceCount: number;
  /** Devices whose latest report carried at least one error entry. */
  failingDeviceCount: number;
  devices: DeviceSyncStatusItem[];
}

export interface DeviceSyncStatusItem {
  /** Id of the latest report, not of the device - devices are identified by MAC address. */
  id: string;
  macAddress: string;
  /** Null when the reported MAC address matches no registered health injector. */
  healthInjectorId: string | null;
  /** False when the reporting phone named a MAC that never completed a connect. */
  injectorMatched: boolean;
  /** Animals still queued on the device - the hanging requests. */
  pendingCount: number;
  /** Animals the device says it has sent. The device's own tally, not the backend's. */
  sentCount: number;
  /** When the device took the snapshot, on the device's own clock. */
  reportedAt: string;
  /** When the backend received it. Diverges from `reportedAt` under device clock drift. */
  receivedAt: string;
  /**
   * Start of the unbroken run of reports in which this device has had something queued. Null when the
   * latest report is drained. Bounded by the API's history window, so a very old value means "at least
   * this long".
   */
  pendingSince: string | null;
  /**
   * Error identifier to the number of animals that hit it, as the device reported it. The keys are the
   * mobile app's own vocabulary and the backend stores them without validating - render them as opaque
   * labels, do not try to translate them.
   */
  errors: Record<string, number> | null;
}

/**
 * How worried to be about one device. Read off how long the queue has been standing rather than off its
 * size: a phone that has just registered 200 animals in a barn with no signal is normal, and the same 200
 * still sitting there tomorrow is not.
 */
export type DeviceSyncSeverity = 'draining' | 'delayed' | 'stuck';

/** A queue younger than this is simply being worked through. */
export const SYNC_DELAYED_AFTER_HOURS = 2;

/** Past this, the queue has survived a working day and nobody has resolved it. */
export const SYNC_STUCK_AFTER_HOURS = 24;

const HOUR_MS = 60 * 60 * 1000;

export const getSyncSeverity = (item: DeviceSyncStatusItem, now: number = Date.now()): DeviceSyncSeverity => {
  // No run to measure - a device with nothing queued is never worse than draining.
  if (item.pendingCount === 0 || !item.pendingSince) {
    return 'draining';
  }

  const elapsedHours = (now - new Date(item.pendingSince).getTime()) / HOUR_MS;

  if (elapsedHours >= SYNC_STUCK_AFTER_HOURS) {
    return 'stuck';
  }

  return elapsedHours >= SYNC_DELAYED_AFTER_HOURS ? 'delayed' : 'draining';
};

/**
 * How long the queue has been standing, as a coarse duration. Deliberately not `formatLastSeen` from
 * `utils/readerStatus`: that one reads "3h ago", which is right for a heartbeat that arrived and wrong for
 * a backlog that is still there.
 */
export const formatPendingDuration = (pendingSince: string | null, now: number = Date.now()): string => {
  if (!pendingSince) {
    return '—';
  }

  const seconds = Math.max(0, Math.floor((now - new Date(pendingSince).getTime()) / 1000));

  if (seconds < 60) {
    return `${seconds}s`;
  }

  if (seconds < 3600) {
    return `${Math.floor(seconds / 60)}m`;
  }

  if (seconds < 86400) {
    return `${Math.floor(seconds / 3600)}h`;
  }

  return `${Math.floor(seconds / 86400)}d`;
};
